/**
 * ChangeWithReasonModal
 *
 * Modal for changing investment item status or budget with a required reason.
 * This creates an audit trail entry for accountability.
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Spinner, ArrowRight, CurrencyEur, ListChecks } from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  InvestmentItem,
  InvestmentItemStatus,
  INVESTMENT_STATUS_LABELS,
} from "../types/investment";

type ChangeType = "status" | "budget";

interface ChangeWithReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  item: InvestmentItem | null;
  changeType: ChangeType;
  /** Callback when budget decrease triggers transfer opportunity */
  onTransferRequest?: (item: InvestmentItem, freedAmount: number) => void;
}

// Format currency helper
const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

// Get allowed status transitions
const getAllowedStatusTransitions = (
  currentStatus: InvestmentItemStatus
): InvestmentItemStatus[] => {
  const transitions: Record<InvestmentItemStatus, InvestmentItemStatus[]> = {
    [InvestmentItemStatus.ONTWERP]: [
      InvestmentItemStatus.IN_BEOORDELING,
      InvestmentItemStatus.GOEDGEKEURD,
      InvestmentItemStatus.AFGEWEZEN,
    ],
    [InvestmentItemStatus.IN_BEOORDELING]: [
      InvestmentItemStatus.GOEDGEKEURD,
      InvestmentItemStatus.AFGEWEZEN,
      InvestmentItemStatus.ONTWERP,
    ],
    [InvestmentItemStatus.GOEDGEKEURD]: [
      InvestmentItemStatus.IN_PROJECT,
      InvestmentItemStatus.AFGEWEZEN,
      InvestmentItemStatus.IN_BEOORDELING,
    ],
    [InvestmentItemStatus.AFGEWEZEN]: [
      InvestmentItemStatus.ONTWERP,
      InvestmentItemStatus.IN_BEOORDELING,
      InvestmentItemStatus.GOEDGEKEURD,
    ],
    [InvestmentItemStatus.IN_PROJECT]: [
      // A line that's been picked up by a project can only be closed —
      // mirrors the BE allowedTransitions in InvestmentService.
      InvestmentItemStatus.CLOSED,
    ],
    [InvestmentItemStatus.CLOSED]: [], // Terminal state
  };

  return transitions[currentStatus] || [];
};

export const ChangeWithReasonModal = ({
  isOpen,
  onClose,
  onSuccess,
  item,
  changeType,
  onTransferRequest,
}: ChangeWithReasonModalProps) => {
  const { session } = useSession();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Status change state
  const [newStatus, setNewStatus] = useState<InvestmentItemStatus | "">("");

  // Budget change state
  const [newBudget, setNewBudget] = useState("");

  // Reason (required for both)
  const [reason, setReason] = useState("");

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && item) {
      setNewStatus("");
      setNewBudget(item.estimatedCostExclVat?.toString() || "");
      setReason("");
    }
  }, [isOpen, item]);

  const handleSubmit = async () => {
    if (!item) return;

    // Validate reason
    if (!reason.trim() || reason.trim().length < 5) {
      toast.error("Reden moet minimaal 5 karakters bevatten");
      return;
    }

    setIsSubmitting(true);

    try {
      if (changeType === "status") {
        if (!newStatus) {
          toast.error("Selecteer een nieuwe status");
          setIsSubmitting(false);
          return;
        }

        const response = await fetchWithAuth(
          `${API_URL}/investment/items/${item.id}/change-status`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              newStatus,
              reason: reason.trim(),
            }),
          },
          session
        );

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.message || "Failed to change status");
        }

        toast.success(
          `Status gewijzigd naar "${INVESTMENT_STATUS_LABELS[newStatus]}"`
        );
      } else {
        // Budget change
        const budgetValue = parseFloat(newBudget);
        if (isNaN(budgetValue) || budgetValue < 0) {
          toast.error("Voer een geldig budget in");
          setIsSubmitting(false);
          return;
        }

        const oldBudget = item.estimatedCostExclVat || 0;
        const isDecrease = budgetValue < oldBudget;
        const freedAmount = oldBudget - budgetValue;

        const response = await fetchWithAuth(
          `${API_URL}/investment/items/${item.id}/change-budget`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              newBudget: budgetValue,
              reason: reason.trim(),
            }),
          },
          session
        );

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.message || "Failed to change budget");
        }

        toast.success(`Budget gewijzigd naar ${formatCurrency(budgetValue)}`);

        // If budget decreased and we have a transfer callback, offer to transfer
        if (isDecrease && freedAmount > 0 && onTransferRequest) {
          // Update item with new budget for transfer modal
          const updatedItem = { ...item, estimatedCostExclVat: budgetValue };
          onSuccess();
          onClose();
          // Small delay to allow modal to close before opening transfer modal
          setTimeout(() => {
            onTransferRequest(updatedItem, freedAmount);
          }, 100);
          return;
        }
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error changing item:", error);
      toast.error(
        error instanceof Error ? error.message : "Fout bij wijzigen van item"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!item) return null;

  const allowedStatuses = getAllowedStatusTransitions(item.status);
  const isStatusChangeDisabled =
    changeType === "status" && allowedStatuses.length === 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {changeType === "status" ? (
              <>
                <ListChecks className="h-5 w-5 text-moss-primary" />
                Status wijzigen
              </>
            ) : (
              <>
                <CurrencyEur className="h-5 w-5 text-moss-primary" />
                Budget wijzigen
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {changeType === "status"
              ? "Wijzig de status van dit investeringsitem. Een reden is verplicht voor de audit trail."
              : "Wijzig het budget van dit investeringsitem. Een reden is verplicht voor de audit trail."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Item Info */}
          <div className="rounded-md bg-gray-50 p-3">
            <div className="text-sm font-medium text-gray-900">{item.title}</div>
            <div className="mt-1 text-xs text-gray-500">
              Huidig budget: {formatCurrency(item.estimatedCostExclVat || 0)}
            </div>
            <div className="mt-1 text-xs text-gray-500">
              Huidige status: {INVESTMENT_STATUS_LABELS[item.status]}
            </div>
          </div>

          {/* Status Change */}
          {changeType === "status" && (
            <div className="space-y-2">
              <Label>Nieuwe status *</Label>
              {isStatusChangeDisabled ? (
                <div className="rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-orange-700">
                  Dit item kan niet meer van status worden gewijzigd omdat het
                  al in een project is opgenomen.
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="flex-1 rounded-md border bg-gray-50 px-3 py-2 text-sm">
                    {INVESTMENT_STATUS_LABELS[item.status]}
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                  <Select
                    value={newStatus}
                    onValueChange={(value) =>
                      setNewStatus(value as InvestmentItemStatus)
                    }
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Selecteer status..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allowedStatuses.map((status) => (
                        <SelectItem key={status} value={status}>
                          {INVESTMENT_STATUS_LABELS[status]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {/* Budget Change */}
          {changeType === "budget" && (
            <div className="space-y-2">
              <Label htmlFor="newBudget">Nieuw budget (excl. BTW) *</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 rounded-md border bg-gray-50 px-3 py-2 text-sm">
                  {formatCurrency(item.estimatedCostExclVat || 0)}
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    €
                  </span>
                  <Input
                    id="newBudget"
                    type="number"
                    value={newBudget}
                    onChange={(e) => setNewBudget(e.target.value)}
                    className="pl-7"
                    min={0}
                    step={100}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              {item.allocatedBudget > 0 && (
                <div className="text-xs text-orange-600">
                  Let op: Er is al {formatCurrency(item.allocatedBudget)}{" "}
                  toegewezen aan projecten. Het nieuwe budget mag niet lager zijn
                  dan dit bedrag.
                </div>
              )}
            </div>
          )}

          {/* Reason (required) */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reden voor wijziging *</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Beschrijf waarom deze wijziging wordt gemaakt..."
              rows={3}
              disabled={isSubmitting}
            />
            <div className="text-xs text-gray-500">
              Minimaal 5 karakters. Deze informatie wordt opgeslagen in de audit
              trail.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Annuleren
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              (changeType === "status" && (!newStatus || isStatusChangeDisabled)) ||
              !reason.trim() ||
              reason.trim().length < 5
            }
            className="bg-moss-primary hover:bg-moss-primaryDark"
          >
            {isSubmitting ? (
              <>
                <Spinner className="mr-2 h-4 w-4 animate-spin" />
                Bezig...
              </>
            ) : (
              "Wijziging opslaan"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
